import { ConfigProvider, theme, type ThemeConfig } from 'antd';
import {
  createContext,
  type PropsWithChildren,
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

type ColorMode = 'light' | 'dark';
export type ThemeVariant = 'default' | 'brasil' | 'hidden';

interface ColorModeContextType {
  mode: ColorMode;
  themeVariant: ThemeVariant;
  setMode: (mode: ColorMode) => void;
  setThemeVariant: (themeVariant: ThemeVariant) => void;
  toggleMode: () => void;
}

export const ColorModeContext = createContext<ColorModeContextType>({} as ColorModeContextType);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const visibleThemeVariants: ThemeVariant[] = ['default', 'brasil'];
const colorModeLocalStorageKey = 'colorMode';
const themeVariantLocalStorageKey = 'themeVariant';
const isColorMode = (mode: string | null): mode is ColorMode => mode === 'light' || mode === 'dark';
const isThemeVariant = (themeVariant: string | null): themeVariant is ThemeVariant =>
  themeVariant === 'default' || themeVariant === 'brasil' || themeVariant === 'hidden';

type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => {
    finished: Promise<void>;
  };
};

const canUseViewTransition = () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return !prefersReducedMotion && Boolean((document as ViewTransitionDocument).startViewTransition);
};

const getSystemPreference = (): ColorMode => {
  const isSystemPreferenceDark = window?.matchMedia('(prefers-color-scheme: dark)').matches;

  return isSystemPreferenceDark ? 'dark' : 'light';
};

const getInitialColorMode = (): ColorMode => {
  const colorModeFromLocalStorage = localStorage.getItem(colorModeLocalStorageKey);

  return isColorMode(colorModeFromLocalStorage) ? colorModeFromLocalStorage : getSystemPreference();
};

const getInitialThemeVariant = (): ThemeVariant => {
  const themeVariantFromLocalStorage = localStorage.getItem(themeVariantLocalStorageKey);

  return isThemeVariant(themeVariantFromLocalStorage) ? themeVariantFromLocalStorage : 'default';
};

const appThemes = {
  default: {
    light: {
      token: {
        colorPrimary: '#4980cc',
      },
    },
    dark: {
      token: {
        colorPrimary: '#4980cc',
      },
    },
  },
  brasil: {
    light: {
      token: {
        colorPrimary: '#059669',
      },
    },
    dark: {
      token: {
        colorPrimary: '#059669',
      },
    },
  },
  hidden: {
    light: {
      token: {
        colorPrimary: '#c084fc',
        colorSuccess: '#10b981',
        colorWarning: '#f59e0b',
      },
    },
    dark: {
      token: {
        colorPrimary: '#c084fc',
        colorSuccess: '#34d399',
        colorWarning: '#fbbf24',
      },
    },
  },
} satisfies Record<ThemeVariant, Record<ColorMode, ThemeConfig>>;

// eslint-disable-next-line react/prop-types
export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [mode, setModeState] = useState<ColorMode>(getInitialColorMode);
  const [themeVariant, setThemeVariant] = useState<ThemeVariant>(getInitialThemeVariant);
  const { darkAlgorithm, defaultAlgorithm } = theme;

  useEffect(() => {
    window.localStorage.setItem(colorModeLocalStorageKey, mode);
  }, [mode]);

  useEffect(() => {
    window.localStorage.setItem(themeVariantLocalStorageKey, themeVariant);
  }, [themeVariant]);

  const setMode = useCallback((nextMode: ColorMode) => {
    const updateColorMode = () => {
      startTransition(() => setModeState(nextMode));
    };

    if (!canUseViewTransition()) {
      setModeState(nextMode);

      return;
    }

    // @metadata ColorModeViewTransition
    // Usa View Transitions para suavizar el cambio de algoritmo claro/oscuro de Ant Design.
    (document as ViewTransitionDocument).startViewTransition?.(updateColorMode);
  }, []);
  const toggleMode = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  useLayoutEffect(() => {
    // Mantiene compatibilidad con selectores existentes y agrega atributos combinables por CSS.
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.setAttribute('data-color-mode', mode);
    document.documentElement.setAttribute('data-theme-variant', themeVariant);
  }, [mode, themeVariant]);

  const selectedTheme = useMemo<ThemeConfig>(
    () => ({
      ...appThemes[themeVariant][mode],
      algorithm: mode === 'light' ? defaultAlgorithm : darkAlgorithm,
    }),
    [darkAlgorithm, defaultAlgorithm, mode, themeVariant],
  );

  return (
    <ColorModeContext.Provider
      value={{
        setMode,
        setThemeVariant,
        themeVariant,
        toggleMode,
        mode,
      }}>
      <ConfigProvider theme={selectedTheme}>{children}</ConfigProvider>
    </ColorModeContext.Provider>
  );
};
